import React, {useMemo} from 'react';
import {Navigate, Route, Routes, useParams} from 'react-router-dom';

import {ErrorBoundary} from '@sentry/react';
import {USER_STATES} from 'src/constants';
import {STATSIG_GATES} from 'src/contexts/statsig-contex';
import {useConferenceAccountCheck} from 'src/hooks/useConferenceAccountCheck';
import {ErrorPage} from 'src/modules/error-page/error-page.components';

import {useGateValue} from '@pb/kit-lib-statsig';

import {ConferenceUsersTab} from './conference-users-tab';
import {GroupsTab} from './groups-tab';
import {UploadListTab} from './upload-list';
import {UsersTab} from './users-tab';

type Props = {
    accountId: number,
};

const RoutedUsersTab = ({accountId}: Props) => {
    const {userType} = useParams<{userType: string}>();
    const provisioningEnabled = useGateValue(STATSIG_GATES.ACCOUNT_PROVISIONING_HISTORY);
    return userType !== 'all' && (provisioningEnabled || !userType || !Object.hasOwn(USER_STATES, userType))
        ? <Navigate replace={true} to={`/profile/account/${accountId}/users-groups/all`}/>
        : <UsersTab/>;
};

export const UsersGroupsTab = ({accountId}: Props) => {
    const isConferenceAccount = useConferenceAccountCheck();

    const redirectUrl = useMemo(() => (isConferenceAccount
        ? `/profile/account/${accountId}/users-groups/virtual`
        : `/profile/account/${accountId}/users-groups/all`
    ), [isConferenceAccount, accountId]);

    return (
        <ErrorBoundary
            fallback={<ErrorPage subtitle="Please try again later." title="User & Groups page is unavailable"/>}>
            <Routes>
                <Route path="/groups" element={<GroupsTab/>}/>
                <Route path="/virtual" element={<ConferenceUsersTab/>}/>
                <Route path="/upload-list" element={<UploadListTab/>}/>
                <Route path="/:userType" element={<RoutedUsersTab accountId={accountId}/>}/>

                <Route
                    path="*"
                    element={<Navigate replace={true} to={redirectUrl}/>}
                />
            </Routes>
        </ErrorBoundary>
    );
};
